/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiImage,
  EuiLink,
  EuiListGroup,
  EuiListGroupItem,
  EuiSpacer,
  EuiText,
  EuiToolTip,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import { projectCpsCrumbs } from '@modules/project-user-pages/breadcrumbs'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import type { ProjectName } from '@modules/project-user-api/v1/types'
import { projectCpsLinkUrl } from '@modules/project-user-lib/urls'
import emptyStateIllustration from '@modules/project-components/ProjectCps/files/emptyStateIllustration.svg'
import ProjectCpsNoPermissionCallout from '@modules/project-components/ProjectCps/ProjectCpsNoPermissionCallout'

type Props = {
  projectType: ProjectType
  projectId: ProjectID
  projectName: ProjectName
  hasUpdatePermission: boolean
}

const ProjectCpsEmptyPage: React.FunctionComponent<Props> = ({
  projectType,
  projectId,
  projectName,
  hasUpdatePermission,
}) => {
  const navigate = useNavigate()

  const { euiTheme } = useEuiTheme()

  const successColor = euiTheme.colors.success

  const linkProjectsButton = (
    <EuiButton
      fill={true}
      onClick={() => {
        navigate(projectCpsLinkUrl(projectType, projectId))
      }}
      disabled={!hasUpdatePermission}
      data-test-subj='cps-emptyPage-linkProjectsButton'
    >
      <FormattedMessage
        id='projectCps.emptyState.linkProjectsButton'
        defaultMessage='Link projects'
      />
    </EuiButton>
  )

  const linkProjectsButtonWithTooltip = !hasUpdatePermission ? (
    <EuiToolTip
      content={
        <FormattedMessage
          id='projectCps.noPermissionTooltip'
          defaultMessage='No permission to link projects'
        />
      }
      position='bottom'
    >
      {linkProjectsButton}
    </EuiToolTip>
  ) : (
    linkProjectsButton
  )

  const isElasticsearch = projectType === 'elasticsearch'
  const isSecurity = projectType === 'security'

  const learnMoreLink = (
    // TODO: https://github.com/elastic/cloud-ui/issues/952
    <EuiLink target='_blank' href='https://www.elastic.co/TBD'>
      <FormattedMessage id='projectCps.emptyState.learnMoreLink' defaultMessage='Learn more' />
    </EuiLink>
  )

  const projectNameElement = (
    <EuiText color='accent' component='span' size='s'>
      {projectName}
    </EuiText>
  )

  const getFooterMessage = () => {
    if (isElasticsearch) {
      return (
        <FormattedMessage
          id='projectCps.emptyState.footer.elasticsearch'
          defaultMessage='As an Elasticsearch project, {projectName} can only be linked with Elasticsearch projects. {learnMoreLink}'
          values={{
            learnMoreLink,
            projectName: projectNameElement,
          }}
        />
      )
    }

    if (isSecurity) {
      return (
        <FormattedMessage
          id='projectCps.emptyState.footer.security'
          defaultMessage='As a Security project, {projectName} can only be linked with Observability or Security projects. {learnMoreLink}'
          values={{
            learnMoreLink,
            projectName: projectNameElement,
          }}
        />
      )
    }

    return (
      <FormattedMessage
        id='projectCps.emptyState.footer.observability'
        defaultMessage='As an Observability project, {projectName} can only be linked with Observability or Security projects. {learnMoreLink}'
        values={{
          learnMoreLink,
          projectName: projectNameElement,
        }}
      />
    )
  }

  const cardFooter = <EuiText size='s'>{getFooterMessage()}</EuiText>

  return (
    <div>
      <Breadcrumbs
        breadcrumbs={projectCpsCrumbs({
          projectType,
          projectId,
          projectName,
        })}
      />
      {!hasUpdatePermission && (
        <Fragment>
          <ProjectCpsNoPermissionCallout />
          <EuiSpacer size='s' />
        </Fragment>
      )}
      <EuiSpacer size='l' />

      <EuiFlexGroup alignItems='center' justifyContent='center'>
        <EuiEmptyPrompt
          css={{
            maxWidth: '65em',
            '.euiEmptyPrompt__main': {
              gap: '32px', // Customization request in https://github.com/elastic/cloud-ui/issues/1335
            },
            '.euiEmptyPrompt__content': {
              paddingBlock: 0, // Customization request in https://github.com/elastic/cloud-ui/issues/1335
            },
          }}
          layout='horizontal'
          title={
            <h2>
              <FormattedMessage
                id='projectCps.emptyState.title'
                defaultMessage='Get started with cross-project search'
              />
            </h2>
          }
          color='plain'
          icon={
            <EuiImage
              size='fullWidth'
              src={emptyStateIllustration}
              alt='Illustration for cross-project search'
            />
          }
          body={
            <Fragment>
              <EuiSpacer size='s' /> {/* Requested to increase spacing between title and body */}
              <EuiFlexGroup gutterSize='xs'>
                <EuiListGroup css={{ marginLeft: 0 }} style={{ marginInlineStart: 0 }} flush={true}>
                  <EuiListGroupItem
                    iconProps={{ color: successColor }}
                    wrapText={true}
                    label={
                      <EuiText>
                        {isElasticsearch ? (
                          <FormattedMessage
                            id='projectCps.emptyState.body.broaden.elasticsearch'
                            defaultMessage='Broaden your dataset by linking additional Elasticsearch projects.'
                          />
                        ) : (
                          <FormattedMessage
                            id='projectCps.emptyState.body.broaden.securityObservability'
                            defaultMessage='Broaden your dataset by linking additional Security and Observability projects.'
                          />
                        )}
                      </EuiText>
                    }
                    iconType='check'
                  />
                  <EuiListGroupItem
                    iconProps={{ color: successColor }}
                    wrapText={true}
                    label={
                      <EuiText>
                        <FormattedMessage
                          id='projectCps.emptyState.body.surfaceData'
                          defaultMessage='Automatically surface data from linked projects in your existing dashboards and searches, with no changes required.'
                        />
                      </EuiText>
                    }
                    iconType='check'
                  />

                  <EuiListGroupItem
                    iconProps={{ color: successColor }}
                    wrapText={true}
                    label={
                      <EuiText>
                        <FormattedMessage
                          id='projectCps.emptyState.body.keepData'
                          defaultMessage='Keep data where it belongs, whether for compliance, cost, or organizational needs, and analyze it all from a single place.'
                        />
                      </EuiText>
                    }
                    iconType='check'
                  />
                </EuiListGroup>
              </EuiFlexGroup>
            </Fragment>
          }
          actions={
            <EuiFlexGroup>
              {linkProjectsButtonWithTooltip}
              <EuiButtonEmpty
                // TODO: https://github.com/elastic/cloud-ui/issues/952
                href='https://www.elastic.co/TBD'
                target='_target'
                iconType='popout'
                iconSide='right'
                color='primary'
                data-test-subj='cps-emptyPage-learnMoreButton'
              >
                <FormattedMessage
                  id='projectCps.emptyState.learnMoreButton'
                  defaultMessage='Learn more'
                />
              </EuiButtonEmpty>
            </EuiFlexGroup>
          }
          footer={cardFooter}
        />
      </EuiFlexGroup>
    </div>
  )
}

export default ProjectCpsEmptyPage
